'use client';

import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';

import { cn } from '@skybeam/shared-utils/index';
import { Button } from '@simulmedia/ui-kit';

import { Play } from 'lucide-react';
import { VideoModal, convertToEmbedUrl } from './video-modal';

import KingsHawaiianColoredLogo from '../../../../public/kings-hawaiian-logo-colored.svg';
import StoryBugLogo from '../../../../public/storybug-logo.svg';
import BrendonWatsonAvatar from '../../../../public/brendon-watson-avatar.svg';
import EricFischerAvatar from '../../../../public/eric-fischer-avatar.svg';
import LynnwoodLogo from '../../../../public/lynnwood-washington-logo.svg';
import SilkPharmacyLogo from '../../../../public/silk-pharmacy-logo.svg';
import HallmarkLogo from '../../../../public/hallmark-logo.svg';
import ManzellaLogo from '../../../../public/manzella-marketing-logo.svg';
import KingsHawaiianLogo from '../../../../public/kings-hawaiian-logo.svg';
import MVCLogo from '../../../../public/mvc-logo.svg';

/** Partner logos in carousel order (duplicated in DOM for infinite scroll). */
const partnerCarouselEntries = [
  { Logo: LynnwoodLogo, slug: 'lynnwood-washington' },
  { Logo: SilkPharmacyLogo, slug: 'silk-pharmacy' },
  { Logo: HallmarkLogo, slug: 'hallmark' },
  { Logo: ManzellaLogo, slug: 'manzella-marketing' },
  { Logo: KingsHawaiianLogo, slug: 'kings-hawaiian' },
  { Logo: MVCLogo, slug: 'metro-vein-centers' },
] as const;

const duplicatedPartnerEntries = [
  ...partnerCarouselEntries,
  ...partnerCarouselEntries,
];

const testimonials = [
  {
    name: 'Brendon Watson',
    title: 'CEO of Story Bug',
    text: 'We really recommend Skybeam for any brand that wants to test local or national TV ads. The self-serve portal was easy to use and gave us great data and insights.',
    avatar: <BrendonWatsonAvatar />,
    logo: <StoryBugLogo />,
    thumbnail: '/testimonial-image.png',
    video: 'https://www.youtube.com/watch?v=ExpmIC22_BU',
  },
  {
    name: 'Eric Fischer',
    title: 'Agency',
    text: 'Once you’re in the platform, it’s pretty self-evident… The most differentiating is choosing the streaming partners and optimizing unduplicated reach at a base-level frequency.',
    avatar: <EricFischerAvatar />,
    logo: <KingsHawaiianColoredLogo />,
    thumbnail: '/testimonial-image.png',
    video: 'https://www.youtube.com/watch?v=ExpmIC22_BU',
  },
];

export const TestimonialSection = () => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | undefined>(undefined);
  const positionRef = useRef(0);

  const [activeVideoUrl, setActiveVideoUrl] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inlineVideoUrl, setInlineVideoUrl] = useState<string | null>(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleVideoClick = (videoUrl: string) => {
    const viewportWidth = window.innerWidth;

    if (viewportWidth >= 960) {
      setActiveVideoUrl(videoUrl);
      setIsModalOpen(true);
      setInlineVideoUrl(null);
    } else {
      setInlineVideoUrl(videoUrl);
      setActiveVideoUrl(null);
      setIsModalOpen(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setActiveVideoUrl(null);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 960 && inlineVideoUrl) {
        setInlineVideoUrl(null);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [inlineVideoUrl]);

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const updateAnimation = () => {
      const totalWidth = slider.scrollWidth;
      const firstSetWidth = totalWidth / 2;

      const speed = firstSetWidth / (55 * 40);

      const animate = () => {
        positionRef.current -= speed;

        if (Math.abs(positionRef.current) >= firstSetWidth) {
          positionRef.current = 0;
        }

        slider.style.transform = `translateX(${positionRef.current}px)`;
        animationRef.current = requestAnimationFrame(animate);
      };

      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }

      positionRef.current = 0;
      animationRef.current = requestAnimationFrame(animate);
    };

    const resizeObserver = new ResizeObserver(() => {
      updateAnimation();
    });

    resizeObserver.observe(slider);

    updateAnimation();

    return () => {
      resizeObserver.disconnect();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [partnerCarouselEntries.length]);

  return (
    <div
      data-test="success-stories-section"
      data-section="testimonial-section"
      className="tablet:py-20 desktop:py-24 flex w-full justify-center overflow-hidden bg-white px-4 py-14"
    >
      <div className="tablet:max-w-[520px] desktop:gap-y-16 laptop:gap-y-19 laptop:max-w-[880px] tablet:gap-y-13 desktop:max-w-[1180px] flex w-full flex-col items-center gap-y-7">
        <h2
          className="heading-marketing-2 font-light"
          data-test="success-stories-heading"
        >
          Success Stories
        </h2>
        <div className="desktop:max-w-[944px] laptop:flex-row tablet:justify-between flex w-full max-w-full flex-col gap-4">
          <div className="desktop:min-w-[464px] laptop:min-w-[431px] relative aspect-272/364 size-full overflow-hidden rounded-2xl">
            {inlineVideoUrl && isMounted ? (
              <div className="relative size-full">
                <iframe
                  src={convertToEmbedUrl(inlineVideoUrl)}
                  className="h-full w-full"
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  title={`${testimonials[0].name} Testimonial`}
                />
              </div>
            ) : (
              <div
                onClick={() => handleVideoClick(testimonials[0].video)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleVideoClick(testimonials[0].video);
                  }
                }}
                className="group relative block size-full cursor-pointer"
                data-test="success-stories-brendon-video"
                role="button"
                tabIndex={0}
                aria-label={`Play ${testimonials[0].name} testimonial video`}
              >
                <div className="relative size-full">
                  <div
                    key={testimonials[0].name}
                    className={cn(
                      'absolute inset-0 transition-transform duration-500 ease-in-out',
                    )}
                  >
                    <Image
                      className="object-cover"
                      src={testimonials[0].thumbnail}
                      alt={`${testimonials[0].name} Testimonial`}
                      fill
                    />
                  </div>
                </div>
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 z-20 rounded-md opacity-0 shadow-[inset_0_0_30px_rgba(0,0,0,0.5)] transition-opacity duration-300 group-hover:opacity-100"
                />
                <div className="tablet:justify-between tablet:p-8 absolute bottom-0 z-20 flex w-full items-center justify-center pb-6">
                  <div className="tablet:block hidden text-[18px] text-white">
                    <div className="font-bold">{testimonials[0].name}</div>
                    <div>{testimonials[0].title}</div>
                  </div>
                  <Button
                    className="rounded-3xl px-6"
                    variant="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleVideoClick(testimonials[0].video);
                    }}
                  >
                    <Play className="fill-white" />
                    Watch Full Story
                  </Button>
                </div>
              </div>
            )}
          </div>
          <div className="desktop:min-w-[464px] laptop:min-w-[431px] desktop:py-7 desktop:px-8 laptop:pt-8.5 laptop:pb-5 laptop:px-8.5 tablet:px-10 tablet:pt-7.5 tablet:pb-5.5 flex flex-col rounded-2xl border border-blue-50 px-5.5 pt-6.5 pb-7.5 shadow-lg">
            {testimonials.map(({ avatar, logo, name, text, title }, index) => {
              const applySeparatorStyles = index % 2 === 0;
              const blockTestId =
                name === 'Brendon Watson'
                  ? 'success-stories-story-bug-block'
                  : 'success-stories-kings-hawaiian-block';
              const logoTestId =
                name === 'Brendon Watson'
                  ? 'success-stories-story-bug-logo'
                  : 'success-stories-kings-hawaiian-logo';
              const photoTestId =
                name === 'Brendon Watson'
                  ? 'success-stories-brendon-photo'
                  : 'success-stories-eric-photo';

              return (
                <div
                  key={name}
                  data-test={blockTestId}
                  className={cn(
                    'tablet:gap-y-9.5 laptop:gap-y-8 desktop:gap-y-8.5 flex flex-col gap-y-5',
                    applySeparatorStyles
                      ? 'desktop:pb-6 laptop:pb-3.5 tablet:pb-4 border-b border-blue-100 pb-8'
                      : 'desktop:pt-6 laptop:pt-3.5 tablet:pt-4 pt-8',
                  )}
                >
                  <div className="tablet:gap-y-3 laptop:gap-y-2.5 flex flex-col gap-y-1.5">
                    <div data-test={logoTestId}>{logo}</div>
                    <div
                      className="tablet:text-lg laptop:text-base text-sm"
                      data-test={`${blockTestId}-quote`}
                    >
                      “{text}”
                    </div>
                  </div>
                  <div className="laptop:gap-x-3 tablet:gap-x-3.5 flex items-center">
                    <div
                      className="tablet:block hidden"
                      data-test={photoTestId}
                    >
                      {avatar}
                    </div>
                    <div>
                      <div
                        className="laptop:text-sm text-base font-bold"
                        data-test={`${blockTestId}-name`}
                      >
                        {name}
                      </div>
                      <div
                        className="tablet:text-base laptop:text-sm text-sm"
                        data-test={`${blockTestId}-title`}
                      >
                        {title}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div
          className="tablet:mt-8 desktop:mt-12.5 -mx-4 mt-5 w-[calc(100%+2rem)]"
          data-test="success-stories-partners-slider"
        >
          <div
            className="relative overflow-hidden"
            data-test="success-stories-partners-slider-viewport"
          >
            <div
              ref={sliderRef}
              className="tablet:gap-x-14 laptop:gap-x-18.5 inline-flex items-center gap-x-9 whitespace-nowrap will-change-transform"
              data-test="success-stories-partners-track"
            >
              {duplicatedPartnerEntries.map(({ Logo, slug }, index) => (
                <div
                  key={`${slug}-${index}`}
                  className="shrink-0"
                  data-test={`success-stories-slider-${slug}`}
                >
                  <Logo />
                </div>
              ))}
            </div>
            <div className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-linear-to-r from-white to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-linear-to-l from-white to-transparent" />
          </div>
        </div>
      </div>
      <VideoModal
        open={isModalOpen}
        videoUrl={activeVideoUrl}
        onClose={handleCloseModal}
        iframeTitle="Video Testimonial from StoryBug"
        modalTestId="success-stories-video-modal"
        iframeTestId="success-stories-video-iframe"
      />
    </div>
  );
};
